<template>
  <div>

    <v-expand-transition v-for="input, i in form.inputs" :key="i">
      <div v-if="!input.conditional || canYouMakeIt">

        <div class="title preserve-newlines mt-9" v-bind:class="{ required: input.required, 'grey--text': disabled }">{{input.title}}</div>

        <div v-if="input.subtitle" class="subtitle preserve-newlines" v-bind:class="{ 'grey--text': disabled }">{{input.subtitle}}</div>

        <v-radio-group
          v-if="input.type == 'RADIO'"
          class="mt-2 py-0"
          hide-details
          :disabled="disabled"
          :row="input.row"
          :rules="[ v => !input.required || !!v  ]"
          :value="response[input.title]"
          @change="v => $set(response, input.title, v)"
        >
          <v-radio
            v-for="choice in input.choices" :key="input.title + ':' + choice"
            :label="choice"
            :value="choice"
          />
        </v-radio-group>

        <div v-else-if="input.type == 'ATTENDEE_SEPARATE_COUNTS'" class="mt-2">
          <int-field
            ref="adults"
            class="mb-3"
            label="Adults"
            style="width: 180px;"
            :disabled="disabled"
            :min="0"
            :max="form.n_rsvp - response[input.title].child"
            :rules="[ v => !!v || (v + response[input.title].child > 0) ]"
            v-model="response[input.title].adult"
            @input="$refs.children[0].$refs.input.validate()"
          />
          <br>
          <int-field
            ref="children"
            label="Children"
            style="width: 180px;"
            :disabled="disabled"
            :min="0"
            :max="form.n_rsvp - response[input.title].adult"
            :rules="[ v => !!v || (v + response[input.title].adult > 0) ]"
            v-model="response[input.title].child"
            @input="$refs.adults[0].$refs.input.validate()"
          />
        </div>

        <int-field
          v-else-if="input.type == 'INT'"
          class="mt-2"
          style="width: 180px;"
          :disabled="disabled"
          :required="input.required"
          :min="input.min"
          :max="input.max"
          :value="response[input.title]"
          @input="v => $set(response, input.title, v)"
        />

        <v-textarea
          v-else-if="input.type == 'TEXT'"
          class="mt-2 white"
          outlined hide-details no-resize rows="5"
          :placeholder="input.placeholder"
          :disabled="disabled"
          :rules="[ v => !input.required || !!v  ]"
          :value="response[input.title]"
          @input="v => $set(response, input.title, v)"
        />

        <v-text-field
          v-else-if="input.type == 'TEXT_SHORT'"
          class="mt-2 white"
          outlined dense hide-details
          :placeholder="input.placeholder"
          :disabled="disabled"
          :rules="[ v => !input.required || !!v  ]"
          :value="response[input.title]"
          @input="v => $set(response, input.title, v)"
        />

        <div v-else-if="input.type == 'ATTENDEE_NAMES'" class="mt-2">
          <div v-for="attendee, i in response['Attendees']" class="my-1 d-flex align-center">
            <v-text-field
              class="mr-3 white"
              dense outlined hide-details placeholder="Name"
              :disabled="disabled"
              :rules="[ v => !!v ]"
              v-model="attendee.name"
            >
              <template v-slot:append>
                <v-btn
                  v-if="form.separate_counts"
                  style="margin-top: -2px;"
                  small icon color="primary"
                  :disabled="disabled"
                  @click="$set(attendee, 'type', (attendee.type == 'adult') ? 'child' : 'adult')"
                >
                  <v-icon v-html="(attendee.type == 'adult') ? mdiHumanMale : iconPathChild" />
                  <span class="btn-tooltip left">{{(attendee.type == 'adult') ? 'Adult' : 'Child'}}</span>
                </v-btn>
              </template>
            </v-text-field>
            <v-btn
              class="mr-n2"
              color="warning" icon
              :disabled="disabled || (response['Attendees'].length == 1)"
              @click="response['Attendees'].splice(i, 1)"
            >
              <v-icon v-html="mdiMinus" />
            </v-btn>
          </div>
          <div v-if="response['Attendees'].length < form.n_rsvp" class="text-right mr-n2">
            <v-menu v-if="showAdd" :activator="$refs.add[0].$el" v-model="showAdd">
              <v-list dense>
                <v-list-item
                  v-for="[ type, icon ] in [ [ 'adult', mdiHumanMale ], [ 'child',iconPathChild  ] ]"
                  :key="type"
                  :disabled="disabled"
                  @click="response['Attendees'].push({ name: '', type })"
                >
                  <v-list-item-title class="mr-3">Add {{type}}</v-list-item-title>
                  <v-icon color="primary" v-html="icon" />
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              ref="add"
              color="primary" icon
              :disabled="disabled"
              @click="form.separate_counts ? (showAdd = true) : response['Attendees'].push({ name: '', type: 'adult' })"
            >
              <v-icon v-html="mdiPlus" />
            </v-btn>
          </div>
        </div>

      </div>
    </v-expand-transition>

  </div>
</template>


<style scoped>
.title {
  line-height: 1.2em;
  color: #000;
}
.title.required:after {
  content: ' *';
  color: #ff5252;
  vertical-align: top;
}
.subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 20px;
}
</style>


<script>
import IntField from '@/components/IntField.vue';
import iconPathChild from 'raw-loader!@/assets/icon-path-child.txt';
import { mdiHumanMale, mdiMinus, mdiPlus } from '@mdi/js';


export default {
  name: 'form-rsvp',

  components: {
    IntField
  },

  props: {
    form: Object,
    response: Object,
    disabled: Boolean
  },

  data() {
    return {
      showAdd: false
    };
  },

  computed: {
    canYouMakeIt() {
      return this.response['Can you make it?'] == 'Yes';
    }
  },

  created() {
    Object.assign(this, { iconPathChild, mdiHumanMale, mdiMinus, mdiPlus });
  }
}
</script>
